import * as React from 'react';
import { useState, useEffect } from 'react';
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Typography,
} from '@mui/material';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import TableChartIcon from '@mui/icons-material/TableChart';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFnsV3';
import { fi } from 'date-fns/locale';
import { withTranslation } from 'react-i18next';
import {
  downloadInventoryReport,
  endOfPreviousMonth,
  endOfPreviousYear,
  toDateString,
} from './inventoryReports';

/**
 * Pick a date and download the inventory value as it stood then.
 *
 * The workshop does not have to remember to take the report on the last day of
 * the month any more; it can be produced afterwards for the accountant.
 */
function InventoryValueReportDialog({ t, open, onClose, onError }) {
  const [date, setDate] = useState(() => endOfPreviousMonth());
  const [busy, setBusy] = useState(null);

  useEffect(() => {
    if (open) {
      setDate(endOfPreviousMonth());
      setBusy(null);
    }
  }, [open]);

  const today = new Date();
  const isValid = Boolean(date) && !Number.isNaN(date?.getTime()) && date <= today;

  const handleDownload = async (format) => {
    if (!isValid) return;
    setBusy(format);
    try {
      await downloadInventoryReport('value', format, { as_of_date: toDateString(date) });
      onClose();
    } catch (err) {
      console.error('Dated inventory value download failed:', err);
      onError(t('inventory.valueReportDated.downloadFailed'));
    } finally {
      setBusy(null);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={busy ? undefined : onClose}
      maxWidth="xs"
      fullWidth
      data-testid="inventory-dated-value-dialog"
    >
      <DialogTitle>{t('inventory.valueReportDated.title')}</DialogTitle>
      <DialogContent>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          {t('inventory.valueReportDated.description')}
        </Typography>

        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 2 }}>
          <Chip
            label={t('inventory.valueReportDated.quickPreviousMonth')}
            onClick={() => setDate(endOfPreviousMonth())}
            variant="outlined"
            size="small"
            data-testid="inventory-dated-value-quick-prev-month"
          />
          <Chip
            label={t('inventory.valueReportDated.quickPreviousYear')}
            onClick={() => setDate(endOfPreviousYear())}
            variant="outlined"
            size="small"
          />
          <Chip
            label={t('inventory.valueReportDated.quickToday')}
            onClick={() => setDate(new Date())}
            variant="outlined"
            size="small"
          />
        </Box>

        <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={fi}>
          <DatePicker
            label={t('inventory.valueReportDated.dateLabel')}
            value={date}
            onChange={(newValue) => setDate(newValue)}
            format="dd.MM.yyyy"
            maxDate={today}
            slotProps={{
              textField: {
                size: 'small',
                fullWidth: true,
                inputProps: { 'data-testid': 'inventory-dated-value-date' },
              },
            }}
          />
        </LocalizationProvider>
      </DialogContent>
      <DialogActions sx={{ flexWrap: 'wrap', gap: 1, px: 3, pb: 2 }}>
        <Button onClick={onClose} disabled={Boolean(busy)}>
          {t('Cancel')}
        </Button>
        <Button
          variant="contained"
          startIcon={busy === 'pdf' ? <CircularProgress size={16} /> : <PictureAsPdfIcon />}
          onClick={() => handleDownload('pdf')}
          disabled={!isValid || Boolean(busy)}
          data-testid="inventory-dated-value-pdf"
        >
          {t('inventory.downloadPdf')}
        </Button>
        <Button
          variant="contained"
          startIcon={busy === 'xlsx' ? <CircularProgress size={16} /> : <TableChartIcon />}
          onClick={() => handleDownload('xlsx')}
          disabled={!isValid || Boolean(busy)}
          data-testid="inventory-dated-value-xlsx"
        >
          {t('inventory.downloadXlsx')}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export default withTranslation()(InventoryValueReportDialog);
